import { createContext, createElement, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { monaco } from "../monaco.ts";
import { useRelay } from "../relayContext.tsx";
import type { OpenFiles } from "../useOpenFiles.ts";
import { applyWorkspaceEdit, type LspBinding } from "./binding.ts";
import { LspClient, type LspClientState } from "./lspClient.ts";
import { bindProject, modelUri, refreshMarkers } from "./providers.ts";
import { startRelaySession } from "./relayTransport.ts";
import { lspLanguageIdForPath, type LspTextEdit } from "./types.ts";
import type { ProblemItem } from "./diagnosticsStore.ts";

export interface ProjectLsp {
  state: LspClientState;
  message: string | undefined;
  problems: ProblemItem[];
  /** Opens the file (if necessary) and jumps to the diagnostic (Problems panel click). */
  reveal: (p: ProblemItem) => void;
  restart: () => void;
}

const IDLE: ProjectLsp = { state: "idle", message: undefined, problems: [], reveal: () => undefined, restart: () => undefined };
const LspContext = createContext<ProjectLsp>(IDLE);
export const useProjectLspState = (): ProjectLsp => useContext(LspContext);

/**
 * Wires ONE Project's open Monaco models to the Agent-side language server: lifecycle (open / change / save / close),
 * Monaco providers, inline markers and the diagnostics the Problems panel shows. The Agent-side server is the only source
 * of semantic TS/JS intelligence. Leaving the Project disposes the client, which stops the Agent process.
 */
export function useProjectLsp(machineId: string, projectId: string, projectName: string, files: OpenFiles): ProjectLsp {
  const relay = useRelay();
  const [state, setState] = useState<LspClientState>("idle");
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [problems, setProblems] = useState<ProblemItem[]>([]);
  const filesRef = useRef(files);
  filesRef.current = files;
  const clientRef = useRef<LspClient | null>(null);

  const client = useMemo(
    () =>
      new LspClient({
        projectId,
        projectName,
        start: () => startRelaySession(relay, machineId, projectId),
        onState: (s, m) => {
          setState(s);
          setMessage(m);
        },
        applyEdit: (edit) => applyWorkspaceEdit(bindingRef(), edit),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [relay, machineId, projectId, projectName],
  );
  const bindingRef = (): LspBinding => binding;
  const binding: LspBinding = useMemo(
    () => ({
      machineId,
      projectId,
      client,
      ensureOpen: (paths) => filesRef.current.ensureOpen(paths),
      ensureModels: (paths) => filesRef.current.ensureOpen(paths).then(() => undefined),
      applyEdits: (path, edits: LspTextEdit[]) => {
        const model = monaco.editor.getModel(modelUri(machineId, projectId, path));
        if (!model) return false;
        model.pushEditOperations(
          [],
          edits.map((e) => ({
            range: new monaco.Range(e.range.start.line + 1, e.range.start.character + 1, e.range.end.line + 1, e.range.end.character + 1),
            text: e.newText,
          })),
          () => null,
        );
        return true;
      },
      reveal: (path, range) => filesRef.current.reveal(path, range),
      report: (m) => setMessage(m),
    }),
    [machineId, projectId, client],
  );

  // Providers + markers, bound for as long as this Project is open.
  useEffect(() => {
    clientRef.current = client;
    const unbind = bindProject(binding);
    const off = client.diagnostics.subscribe(() => setProblems(client.diagnostics.problems()));
    return () => {
      off();
      unbind();
      client.dispose(); // stops the Agent-side process
      clientRef.current = null;
      setProblems([]);
    };
  }, [client, binding]);

  // A reconnect of the relay socket drops the Agent session: start a fresh one (no attempt to resume).
  useEffect(() => relay.onReconnect(() => client.connectionLost()), [relay, client]);

  // Document lifecycle: attach / detach models as tabs come and go; one change subscription per model.
  const subs = useRef(new Map<string, { model: monaco.editor.ITextModel; dispose: () => void }>());
  useEffect(() => {
    const current = new Set<string>();
    for (const f of files.files) {
      if (lspLanguageIdForPath(f.path) === null) continue;
      current.add(f.path);
      const known = subs.current.get(f.path);
      if (known && known.model === f.model) continue;
      known?.dispose();
      const path = f.path;
      const doc = { path, getText: () => f.model.getValue(), version: () => f.model.getVersionId() };
      client.attach(doc);
      const d = f.model.onDidChangeContent(() => client.changed(path));
      subs.current.set(path, { model: f.model, dispose: () => d.dispose() });
      refreshMarkers(binding, path);
    }
    for (const [path, s] of [...subs.current]) {
      if (current.has(path)) continue;
      s.dispose();
      subs.current.delete(path);
      client.detach(path);
    }
  }, [files.files, client, binding]);
  useEffect(
    () => () => {
      for (const s of subs.current.values()) s.dispose();
      subs.current.clear();
    },
    [client],
  );

  useEffect(() => files.onSaved((path) => client.saved(path)), [files, client]);

  return useMemo(
    () => ({
      state,
      message,
      problems,
      reveal: (p) =>
        filesRef.current.reveal(p.path, {
          startLineNumber: p.line,
          startColumn: p.column,
          endLineNumber: p.endLine,
          endColumn: p.endColumn,
        }),
      restart: () => void clientRef.current?.restart(),
    }),
    [state, message, problems],
  );
}

export function LspProvider({ value, children }: { value: ProjectLsp; children: ReactNode }) {
  return createElement(LspContext.Provider, { value }, children);
}
